// @ts-nocheck
export function numberFormatWithComma(val) {
  return val && isFinite(val)
    ? Number(parseFloat(val).toFixed(2)).toLocaleString('en', {
        minimumFractionDigits: 2,
      }) === '-0.00'
      ? '0.00'
      : Number(parseFloat(val).toFixed(2)).toLocaleString('en', {
          minimumFractionDigits: 2,
        })
    : '0.00';
}

export function numberFormat(val) {
  return isFinite(val) ? Number(parseFloat(val)).toFixed(2) : '0.00';
}

/**
 *
 * @param {*} date
 * @returns date in *** dd/mm/yyyy *** format
 */
export function dateFormat(date) {
  return typeof date.getMonth === 'function'
    ? date.toLocaleDateString('en-GB')
    : new Date(date).toLocaleDateString('en-GB');
}
export function dateFormatWithDay(date) {
  const options = {
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  return typeof date.getMonth === 'function'
    ? date.toLocaleDateString('en-GB', options)
    : new Date(date).toLocaleDateString('en-GB', options);
}

/**
 *
 * @param {*} date
 * formats date to yyyy-mm-dd
 */
export function dateFormatWithDash(date) {
  return (
    date &&
    `${date?.getFullYear()}-${(date.getMonth() + 1)?.toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    })}-${date?.getDate()?.toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      useGrouping: false,
    })}`
  );
}

export function dateFormatWithDashWithValidation(dateString) {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    console.error(`Invalid date: ${dateString}`);
    return new Date();
  }

  return `${date?.getFullYear()}-${(date.getMonth() + 1)?.toLocaleString(
    'en-US',
    {
      minimumIntegerDigits: 2,
      useGrouping: false,
    },
  )}-${date?.getDate()?.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })}`;
}

export function setDateTime(date, hour, min, sec, millisec) {
  date.setHours(hour, min, sec, millisec);
  return date;
}

/**
 * formats date to mm/dd hh:mm am
 * @param {*} date
 */

export function dateFormatWithAmPm(date, timeZone) {
  const options = {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  const timeString = new Intl.DateTimeFormat('en-US', options).format(date);

  return `${day}/${month}/${year} ${timeString}`;
}

export function formatDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const day = `00${date.getDate()}`.slice(-2);
  const month = `00${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();
  const hours = `00${date.getHours()}`.slice(-2);
  const minutes = `00${date.getMinutes()}`.slice(-2);
  const seconds = `00${date.getSeconds()}`.slice(-2);

  return `${day}-${month}-${year}_${hours}-${minutes}-${seconds}`;
}

export function formatIsoDateString(isoDateString, timezone) {
  if (!isoDateString) return '';
  const date = new Date(isoDateString);

  const utcDate = new Date(
    date.toLocaleString('en-US', { timeZone: timezone }),
  );

  const day = utcDate.getDate().toString().padStart(2, '0');
  const month = (utcDate.getMonth() + 1).toString().padStart(2, '0');
  const year = utcDate.getFullYear().toString().slice(-2);

  const formattedDate = `${day}/${month}/${year}`;

  const timeOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  };
  const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

  return `${formattedDate}, ${formattedTime}`;
}

export const formatCurrency = (value: number) => {
  if (value === 0) {
    return '-';
  }
  return `$ ${numberFormatWithComma(value)}`;
};

export const formatPercentage = (value: number) => {
  if (value === 0) {
    return '-';
  }
  if (value >= 1 || value <= -1) {
    return `${value.toFixed(1)} %`;
  }
  return `${(value * 100).toFixed(1)} %`;
};

// chart utils
export const formatNumberToCompactLabel = (value: number | null) => {
  if (value === null) {
    return '$ 0';
  }

  const absValue = Math.abs(value);

  if (absValue >= 1000000000) {
    const billions = value / 1000000000;
    return billions % 1 === 0
      ? `$ ${Math.floor(billions)}B`
      : `$ ${billions.toFixed(2)}B`;
  }
  if (absValue >= 1000000) {
    const millions = value / 1000000;
    return millions % 1 === 0
      ? `$ ${Math.floor(millions)}M`
      : `$ ${millions.toFixed(2)}M`;
  }
  if (absValue >= 1000) {
    const thousands = value / 1000;
    return thousands % 1 === 0
      ? `$ ${Math.floor(thousands)}k`
      : `$ ${thousands.toFixed(2)}k`;
  }

  return value % 1 === 0
    ? `$ ${Math.floor(value).toString()}`
    : `$ ${value.toFixed(2)}`;
};

export const formatNumberToCurrency = (
  value: number | null,
  noShorthand: boolean = false,
): string => {
  if (value === null) return '';
  const REMOVE_TRAILING_ZEROS = /\.00$/;
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (noShorthand) {
    return formatter.format(value).replace('$', '$ ');
  }
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000_000) {
    const billions = value / 1_000_000_000;
    return `${formatter
      .format(billions)
      .replace('$', '$ ')
      .replace(REMOVE_TRAILING_ZEROS, '')}B`;
  }
  if (absValue >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${formatter
      .format(millions)
      .replace('$', '$ ')
      .replace(REMOVE_TRAILING_ZEROS, '')}M`;
  }

  return formatter.format(value).replace('$', '$ ');
};
